import { AppBar, Grid, Tab, Tabs } from "@mui/material";
import { isAbortError, isEmpty, toDateString } from "helpers";
import { cloneDeep } from "lodash";
import { FC, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { meterReadingsNotRequiredOrFilled } from "operations/mutations/CompleteVisit";
import { useAppDispatch, useAppSelector } from "store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import { isFlagEnabled } from "store/slices/user.store";
import {
  selectChecklistsIncomplete,
  selectSelectedJob,
  selectSelectedJobVisit,
  selectTravelTabInvalid,
  setVisitValue,
  startVisitWork,
} from "store/slices/visit.store";

import { unwrapResult } from "@reduxjs/toolkit";
import { TabPanel } from "components/TabPanel";
import TabToolbarPlaceholder from "components/TabToolbarPlaceholder";
import { ChecklistTab } from "./ChecklistTab";
import { CompleteVisitButton } from "./CompleteVisitButton";
import { ExtrasTab } from "./ExtrasTab";
import { FileTab } from "./FileTab";
import { MeterTab } from "./MeterTab";
import { NotesTab } from "./NotesTab";
import { PartsTab } from "./PartsTab";
import { TasksTab } from "./TasksTab";
import { TimesTab } from "./TimesTab";
import { VisitTab } from "./VisitTab";
import { SignoffDialog } from "./completion/SignoffDialog";

const errorKeys = {
  visit: ["causeId", "solutionDescription", "actionId1", "actionId2", "actionId3"],
  times: ["travelMileage", "startDate", "startTime", "stopDate", "stopTime"],
  extras: [],
  checklists: [],
  parts: [],
  files: [],
  notes: [],
  meters: [],
  tasks: [],
};

export const JobVisit: FC = () => {
  const dispatch = useAppDispatch();
  const job = useAppSelector(selectSelectedJob);
  const { errors, meterReadings, autoEndTime, workTimes, travelTimes, metersNotSaved } =
    useAppSelector(selectSelectedJobVisit);
  const intl = useIntl();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState("visit");
  const isWorkStarted = useRef(false);
  const extrasEnabled = useAppSelector((s) => isFlagEnabled(s, "Extras"));
  const travelTabInvalid = useAppSelector(selectTravelTabInvalid);
  const checklistsIncomplete = useAppSelector(selectChecklistsIncomplete);
  // FAB
  const [fab, setFab] = useState<JSX.Element>();
  // Signoff
  const [openSignoff, setOpenSignoff] = useState(false);

  const tabHasError = (tabName: keyof typeof errorKeys) => {
    let searchKeys = errorKeys[tabName];
    let currentErrorKeys = Object.keys(errors);
    for (let key of currentErrorKeys) {
      if (searchKeys.some((sub) => key.includes(sub))) return true;
    }
    return false;
  };

  useEffect(() => {
    if (isWorkStarted.current || !job) return;

    if (isEmpty(workTimes) || !workTimes[0].startTime) {
      isWorkStarted.current = true;
      dispatch(startVisitWork({ jobId: job.id }))
        .then(unwrapResult)
        .then(({ queued }) => {
          if (queued) {
            dispatch(addSnackbarMessage({ key: "StartVisitWork-stored" }));
          } else {
            dispatch(addSnackbarMessage({ key: "StartVisitWork-success" }));
          }
        })
        .catch((e) => {
          if (isAbortError(e)) return;
          dispatch(addSnackbarMessage({ key: "StartVisitWork-fail" }));
        });
    } else {
      isWorkStarted.current = true;
    }
  }, [workTimes, isWorkStarted, dispatch, job]);

  const invalidTravelTime =
    !isEmpty(travelTimes) &&
    travelTimes[0].startTime !== null &&
    travelTimes[travelTimes.length - 1].stopTime === null;

  const onCompleteVisit = () => {
    if (invalidTravelTime) {
      dispatch(addSnackbarMessage({ key: "No-end-travel" }));
    } else {
      if (meterReadings && meterReadingsNotRequiredOrFilled(meterReadings, job)) {
        if (autoEndTime) {
          let workTimesCopy = cloneDeep(workTimes);
          let last = workTimesCopy.pop();
          if (last) {
            last.stopDate = toDateString(Date.now());
            last.stopTime = toDateString(Date.now());
            dispatch(setVisitValue({ key: "workTimes", value: [...workTimesCopy, last] }));
          }
        }
        setOpenSignoff(true);
        setTabValue("visit");
      } else {
        dispatch(addSnackbarMessage({ key: "CompleteVisitWithParts-fail-meters" }));
      }
      return null;
    }
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          mt: 7,
        }}
      >
        <Tabs
          scrollButtons
          value={tabValue}
          onChange={(_, newValue: string) => {
            setTabValue(newValue);
          }}
          variant="scrollable"
          textColor="inherit"
        >
          <Tab
            label={
              <>
                {intl.formatMessage({ id: "general.visit" })}
                {tabHasError("visit") ? "*" : null}
              </>
            }
            className="e2e-visit-tab"
            value="visit"
            data-testid="visitTab"
          />
          <Tab
            label={
              <>
                {intl.formatMessage({ id: "job.times" })}
                {tabHasError("times") || travelTabInvalid ? "*" : null}
              </>
            }
            className="e2e-times-tab"
            value="times"
            data-testid="timesTab"
          />
          {extrasEnabled && (
            <Tab
              label={
                <>
                  {intl.formatMessage({ id: "job.extras" })}
                  {tabHasError("extras") ? "*" : null}
                </>
              }
              className="e2e-extras-tab"
              value="extras"
              data-testid="extrasTab"
            />
          )}
          {!!job.checklists?.length && (
            <Tab
              label={
                <>
                  {intl.formatMessage({ id: "job.checklists" })}
                  {tabHasError("checklists") || checklistsIncomplete ? "*" : null}
                </>
              }
              className="e2e-checklists-tab"
              value="checklists"
              data-testid="checklistsTab"
            />
          )}
          <Tab
            label={
              <>
                {intl.formatMessage({ id: "job.parts" })}
                {tabHasError("parts") ? "*" : null}
              </>
            }
            className="e2e-parts-tab"
            value="parts"
            data-testid="partsTab"
          />
          <Tab
            label={
              <>
                {intl.formatMessage({ id: "general.files" })}
                {tabHasError("files") ? "*" : null}
              </>
            }
            className="e2e-files-tab"
            value="files"
            data-testid="filesTab"
          />
          <Tab
            label={
              <>
                {intl.formatMessage({ id: "job.notes" })}
                {tabHasError("notes") ? "*" : null}
              </>
            }
            className="e2e-notes-tab"
            value="notes"
            data-testid="notesTab"
          />
          {job.meters && !!job.meters?.length && (
            <Tab
              label={
                <>
                  {intl.formatMessage({ id: "job.meters" })}
                  {tabHasError("meters") || metersNotSaved ? "*" : null}
                </>
              }
              className="e2e-meters-tab"
              value="meters"
              data-testid="metersTab"
            />
          )}
          {!!job.tasks?.length && (
            <Tab
              label={
                <>
                  {intl.formatMessage({ id: "job.tasks" })}
                  {tabHasError("tasks") ? "*" : null}
                </>
              }
              className="e2e-work-tasks-tab"
              value="tasks"
              data-testid="tasksTab"
            />
          )}
        </Tabs>
      </AppBar>

      <TabToolbarPlaceholder />
      <TabPanel index="visit" value={tabValue}>
        <VisitTab handleFinished={() => navigate(-1)} setFab={setFab} />
      </TabPanel>
      <TabPanel index="times" value={tabValue}>
        <TimesTab setFab={setFab} />
      </TabPanel>
      <TabPanel index="extras" value={tabValue}>
        <ExtrasTab setFab={setFab} />
      </TabPanel>
      <TabPanel index="parts" value={tabValue}>
        <PartsTab setFab={setFab} />
      </TabPanel>
      <TabPanel index="files" value={tabValue}>
        <FileTab setFab={setFab} />
      </TabPanel>
      <TabPanel index="notes" value={tabValue}>
        <NotesTab setFab={setFab} />
      </TabPanel>
      <TabPanel index="meters" value={tabValue}>
        <MeterTab setFab={setFab} />
      </TabPanel>
      <TabPanel index="tasks" value={tabValue}>
        <TasksTab setFab={setFab} />
      </TabPanel>
      <TabPanel index="checklists" value={tabValue}>
        <ChecklistTab addButton setFab={setFab} />
      </TabPanel>
      <Grid
        container
        direction="column"
        width="inherit"
        sx={{
          position: "fixed",
          bottom: 0,
          right: 0,
        }}
      >
        <Grid item>{fab}</Grid>
        <Grid item>
          <CompleteVisitButton onCompleteVisit={onCompleteVisit} setTabValue={setTabValue} />
        </Grid>
      </Grid>
      <SignoffDialog open={openSignoff} handleClose={() => setOpenSignoff(false)} />
    </>
  );
};
